/**
 * useMouseLoggerWithBuffer
 *
 * Amacı:
 * - Fare ve kaydırma etkileşimlerini (mousemove, click, mousedown, mouseup, scroll) dinleyip
 *   hafızada tamponlayarak (buffer) periyodik olarak toplu şekilde sunucuya göndermek.
 * - Gürültüyü azaltmak için mousemove olaylarında basit bir throttle (>=100ms) ve delta (>=5px) filtresi uygular.
 *
 * Parametreler:
 * - assetId: string | number — Log kayıtlarını ilişkilendirmek için kullanılan kimlik.
 *
 * Loglanan Olaylar (eventType) ve Metadata:
 * - 'mousemove' — { x, y, target }
 * - 'click'     — { x, y, target }
 * - 'mousedown' — { x, y, target }
 * - 'mouseup'   — { x, y, target }
 * - 'scroll'    — { x, y } // x=window.scrollX, y=window.scrollY
 *   Not: target, e.target?.tagName bilgisinden türetilir.
 *
 * Davranış ve Kenar Durumları:
 * - Olaylar önce `bufferRef` içine eklenir; 5 saniyede bir `service.createMany` ile gönderilir.
 * - Bileşen unmount olduğunda kalan tampon veriler gönderilir (flush).
 * - mousemove için: ardışık olaylar arasında <100ms veya hem dx<5 hem dy<5 ise atlanır.
 * - SSR ortamında kullanılacaksa window/document kontrolleri gerekir; bu hook tarayıcıda çağrılmalıdır.
 *
 * Kısa Kullanım Örneği:
 *
 * function PlayerPage({ assetId }) {
 *   useMouseLoggerWithBuffer(assetId);
 *   return <Player />;
 * }
 */

import { useEffect, useRef, useCallback } from "react";
import LogService from "../../service/log/LogService";
// import {logEventBus} from "./inactivity/logEventBus";

const service = new LogService('logMouse');

export const useMouseLoggerWithBuffer = (assetId) => {
    const bufferRef = useRef([]);
    const lastMoveRef = useRef({ x: 0, y: 0, time: 0 });

    const log = useCallback(
        (eventType, metadata) => {
            bufferRef.current.push({
                eventType,
                assetId,
                metadata: JSON.stringify(metadata),
            });

            // Etkinliği logEventBus ile yayınla
            // logEventBus.emit({ type: eventType, timestamp: Date.now() });
        },
        [assetId]
    );

    const flushLogs = useCallback(() => {
        const logs = bufferRef.current;
        if (logs.length > 0) {
            service.createMany(logs);
            bufferRef.current = [];
        }
    }, []);

    useEffect(() => {
        const cleanupFns = [];

        const allEvents = [
            { type: "mousemove", includeMousePos: true },
            { type: "click", includeMousePos: true },
            { type: "mousedown", includeMousePos: true },
            { type: "mouseup", includeMousePos: true },
            { type: "scroll", includeMousePos: false },
        ];

        allEvents.forEach(({ type, includeMousePos }) => {
            const handler = (e) => {
                const now = Date.now();
                const metadata = {};

                if (includeMousePos) {
                    const dx = Math.abs(e.clientX - lastMoveRef.current.x);
                    const dy = Math.abs(e.clientY - lastMoveRef.current.y);
                    const dt = now - lastMoveRef.current.time;

                    if (type === "mousemove") {
                        if (dt < 100 || (dx < 5 && dy < 5)) return; // throttle + delta
                        lastMoveRef.current = { x: e.clientX, y: e.clientY, time: now };
                    }

                    metadata.x = e.clientX;
                    metadata.y = e.clientY;
                } else {
                    metadata.x = window.scrollX;
                    metadata.y = window.scrollY;
                }

                metadata.target = e.target?.tagName;

                log(type, metadata);
            };

            window.addEventListener(type, handler);
            cleanupFns.push(() => window.removeEventListener(type, handler));
        });

        const interval = setInterval(flushLogs, 5000); // 5 saniyede bir gönder
        cleanupFns.push(() => clearInterval(interval));

        return () => {
            cleanupFns.forEach((fn) => fn());
            flushLogs(); // komponent unmount olduğunda kalanları gönder
        };
    }, [assetId, log, flushLogs]);
};
